import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';

import ButtonWithLoader from 'components/ButtonWithLoader/ButtonWithLoader';
import SingleLocationDropdown from 'components/SingleLocationDropdown/SingleLocationDropdown';
import SectorsSelector from 'components/Shared/Selectors/SectorsSelector';
import ImageCropper from 'components/ImageCropper2/ImageCropper2';
import Thumbnail from 'components/Thumbnails/Thumbnail';

import { hasAvatar } from 'helpers/HelperFunctions';
import { getDisplaynameFormatError } from 'helpers/displaynameValidation';
import { trackEvent } from 'helpers/Analytics';
import {
  update as updateUser,
  updateLocal as updateLocalUser,
} from 'redux/modules/auth';

import styles from './NextStepsModal.module.scss';
import { Check, ChevronLeft, User } from 'lucide-react';

const NextStepsModal = ({ show, onHide }) => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);

  const [showAvatarUpload, setShowAvatarUpload] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [profileNameError, setProfileNameError] = useState(null);
  const [profileLocation, setProfileLocation] = useState({
    text: '',
    city: '',
    country: '',
    country_code: '',
  });
  const [sectors, setSectors] = useState([]);
  const [avatarData, setAvatarData] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    if (show && auth.user) {
      // Don't show displayname if it's the same as username or if it's 'client'
      const isDefaultName =
        auth.user.displayname === auth.user.username ||
        auth.user.displayname?.toLowerCase() === 'client';

      const name = isDefaultName ? '' : auth.user.displayname;
      setProfileName(name);
      setProfileNameError(getDisplaynameFormatError(name));

      // Don't show location if it's 'Private'
      const locationText = auth.user?.location?.text || '';
      const isPrivateLocation = locationText.toLowerCase() === 'private';

      setProfileLocation({
        text: isPrivateLocation ? '' : locationText,
        city: isPrivateLocation ? '' : auth.user?.location?.city || '',
        country: isPrivateLocation ? '' : auth.user?.location?.country || '',
        country_code: isPrivateLocation
          ? ''
          : auth.user?.location?.country_code || '',
      });
      setSectors(auth.user?.sectors || []);
    }
  }, [show, auth.user]);

  const nameCompleted =
    profileName &&
    !getDisplaynameFormatError(profileName) &&
    profileName !== auth.user?.username &&
    profileName.toLowerCase() !== 'client';
  const locationCompleted =
    profileLocation.text !== '' &&
    profileLocation.text.toLowerCase() !== 'private';
  const avatarCompleted = hasAvatar(auth.user) || avatarData !== null;
  const sectorCompleted = auth.user?.organisation
    ? (auth.user?.sectors && auth.user.sectors.length > 0) ||
      (sectors && sectors.value)
    : true;

  const profileStepsTotal = auth.user?.organisation ? 4 : 3;
  const profileStepsCompleted =
    (nameCompleted ? 1 : 0) +
    (locationCompleted ? 1 : 0) +
    (avatarCompleted ? 1 : 0) +
    (auth.user?.organisation && sectorCompleted ? 1 : 0);

  const profileCompletionPercentage = Math.round(
    (profileStepsCompleted / profileStepsTotal) * 100
  );

  const isProfileComplete =
    profileStepsTotal > 0 && profileStepsCompleted === profileStepsTotal;

  const handleCloseModal = () => {
    setShowAvatarUpload(false);

    // Don't show displayname if it's the same as username or if it's 'client'
    const isDefaultName =
      auth.user.displayname === auth.user.username ||
      auth.user.displayname?.toLowerCase() === 'client';

    const name = isDefaultName ? '' : auth.user.displayname;
    setProfileName(name);
    setProfileNameError(null);

    // Don't show location if it's 'Private'
    const locationText = auth.user?.location?.text || '';
    const isPrivateLocation = locationText.toLowerCase() === 'private';

    setProfileLocation({
      text: isPrivateLocation ? '' : locationText,
      city: isPrivateLocation ? '' : auth.user?.location?.city || '',
      country: isPrivateLocation ? '' : auth.user?.location?.country || '',
      country_code: isPrivateLocation
        ? ''
        : auth.user?.location?.country_code || '',
    });
    setSectors(auth.user?.sectors || []);
    setAvatarData(null);
    onHide();
  };

  const handleSaveProfile = () => {
    if (!auth.user || savingProfile) return;

    const nameError = getDisplaynameFormatError(profileName);

    if (nameError) {
      setProfileNameError(nameError);
      return;
    }

    setSavingProfile(true);

    const updates = {};

    if (profileName && profileName !== auth.user.displayname) {
      updates.displayname = profileName;
    }

    if (
      profileLocation.text &&
      profileLocation.text !== auth.user.location?.text
    ) {
      updates.location = {
        city: profileLocation.city,
        country: profileLocation.country,
        country_code: profileLocation.country_code,
        text: profileLocation.text,
      };
    }

    if (auth.user.organisation) {
      if (sectors && sectors.value) {
        const sectorValue = sectors.value;
        updates.sectors = [sectorValue];
      }
    }

    if (avatarData) {
      updates.avatar = avatarData.avatar;
      updates.crop = avatarData.crop;
    }

    if (Object.keys(updates).length === 0) {
      setSavingProfile(false);
      handleCloseModal();
      return;
    }

    dispatch(updateUser(auth.user, updates))
      .then(() => {
        trackEvent('client-updates-next-steps');
        if (isProfileComplete) {
          trackEvent('client-completes-next-steps');
        }
        setSavingProfile(false);
        handleCloseModal();

        if (avatarData?.croppedImage) {
          dispatch(
            updateLocalUser(auth.user.id, {
              avatars: { temp: avatarData.croppedImage },
            })
          );
        }
      })
      .catch((error) => {
        setSavingProfile(false);
        console.error('Error saving profile:', error);
      });
  };

  return (
    <Modal
      show={show}
      onHide={handleCloseModal}
      dialogClassName={styles.nextStepsModal}
    >
      <Modal.Header closeButton className={styles.nextStepsModalHeader}>
        {showAvatarUpload && (
          <Button
            bsStyle="link"
            onClick={() => setShowAvatarUpload(false)}
            className={styles.modalBackButton}
          >
            <ChevronLeft width={20} height={20} />
            Back
          </Button>
        )}
        {!showAvatarUpload && (
          <Modal.Title className={styles.nextStepsModalTitle}>
            {isProfileComplete ? 'Edit your profile' : 'Complete Your Profile'}
          </Modal.Title>
        )}
      </Modal.Header>
      <Modal.Body className={styles.nextStepsModalBody}>
        <div className={styles.nextStepsModalContent}>
          {showAvatarUpload ? (
            <ImageCropper
              existingImage={auth.user?.avatars}
              onDone={(details) => {
                setAvatarData({
                  avatar: details.image,
                  crop: {
                    avatar: {
                      x: details.x,
                      y: details.y,
                      w: details.width,
                      h: details.height,
                    },
                  },
                  croppedImage: details.croppedImage,
                });
                setShowAvatarUpload(false);
              }}
              buttonAlign="right"
            />
          ) : (
            <>
              {!isProfileComplete && (
                <div className={styles.progressSection}>
                  <div className={styles.progressHeader}>
                    <span className={styles.progressLabel}>Your Progress</span>
                    <span className={styles.progressPercentage}>
                      {profileCompletionPercentage}%
                    </span>
                  </div>
                  <div className={styles.progressBar}>
                    <div
                      className={styles.progressBarFill}
                      style={{ width: `${profileCompletionPercentage}%` }}
                    />
                  </div>
                </div>
              )}

              <div className={styles.formField}>
                <label className={styles.formLabel}>
                  <span>
                    {auth.user.organisation ? 'Company Name' : 'Your Name'}
                  </span>
                  <Check
                    width={16}
                    height={16}
                    className={
                      nameCompleted
                        ? styles.checkIconComplete
                        : styles.checkIconIncomplete
                    }
                  />
                </label>
                <input
                  type="text"
                  placeholder={
                    auth.user.organisation
                      ? 'Enter your company name'
                      : 'Enter your full name'
                  }
                  value={profileName}
                  onChange={(e) => {
                    const value = e.target.value;
                    setProfileName(value);
                    setProfileNameError(getDisplaynameFormatError(value));
                  }}
                  className={styles.formInput}
                  maxLength={40}
                />
                {profileNameError && (
                  <p className="text-danger">{profileNameError}</p>
                )}
              </div>

              <div className={styles.formField}>
                <label className={styles.formLabel}>
                  <span>
                    {auth.user.organisation ? 'Company Logo' : 'Profile Avatar'}
                  </span>
                  <Check
                    width={16}
                    height={16}
                    className={
                      avatarCompleted
                        ? styles.checkIconComplete
                        : styles.checkIconIncomplete
                    }
                  />
                </label>
                <div className={styles.avatarUploadContainer}>
                  <div className={styles.avatarPreview}>
                    {avatarData ? (
                      <img
                        src={avatarData.croppedImage}
                        alt="Avatar"
                        className={styles.avatarImage}
                      />
                    ) : avatarCompleted ? (
                      <Thumbnail
                        thumbnails={auth.user.avatars}
                        circle
                        style={{ width: '64px', height: '64px' }}
                        lazyLoad={false}
                      />
                    ) : (
                      <User
                        width={32}
                        height={32}
                        className={styles.avatarPlaceholder}
                      />
                    )}
                  </div>
                  <Button
                    bsStyle="default"
                    onClick={() => setShowAvatarUpload(true)}
                    className={styles.uploadAvatarButton}
                  >
                    Change {auth.user.organisation ? 'Logo' : 'Avatar'}
                  </Button>
                </div>
              </div>

              <div className={styles.formField}>
                <label className={styles.formLabel}>
                  <span>
                    {auth.user.organisation
                      ? 'Company Location'
                      : 'Your Location'}
                  </span>
                  <Check
                    width={16}
                    height={16}
                    className={
                      locationCompleted
                        ? styles.checkIconComplete
                        : styles.checkIconIncomplete
                    }
                  />
                </label>
                <SingleLocationDropdown
                  defaultLocation={profileLocation.text}
                  onLocationChange={(location) => {
                    setProfileLocation({
                      text: location.text,
                      city: location.city || '',
                      country: location.country,
                      country_code: location.country_code,
                    });
                  }}
                  placeholder="e.g. Manchester, United Kingdom"
                  inputClassName={styles.formInput}
                />
              </div>

              {auth.user?.organisation && (
                <div className={styles.formField}>
                  <label className={styles.formLabel}>
                    <span>Company Sector</span>
                    <Check
                      width={16}
                      height={16}
                      className={
                        sectorCompleted
                          ? styles.checkIconComplete
                          : styles.checkIconIncomplete
                      }
                    />
                  </label>
                  <SectorsSelector
                    existing={auth.user.sectors || []}
                    onSelect={(sectors) => {
                      setSectors(sectors);
                    }}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        borderColor: state.isFocused ? '#0777da' : '#d1d5db',
                        boxShadow: state.isFocused
                          ? '0 0 0 3px rgba(7, 119, 218, 0.1)'
                          : 'none',
                        '&:hover': {
                          borderColor: state.isFocused ? '#0777da' : '#9ca3af',
                        },
                      }),
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </Modal.Body>
      {!showAvatarUpload && (
        <Modal.Footer bsClass={styles.nextStepsModalFooter}>
          <Button
            bsStyle="default"
            onClick={handleCloseModal}
            className={styles.nextStepsModalButtonSecondary}
          >
            Close
          </Button>
          <ButtonWithLoader
            bsStyle="primary"
            onClick={handleSaveProfile}
            disabled={savingProfile}
            loading={savingProfile}
            className={styles.nextStepsModalButtonPrimary}
            text="Save"
          />
        </Modal.Footer>
      )}
    </Modal>
  );
};

NextStepsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
};

export default NextStepsModal;
